import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { useClick2PayIsAllowed } from '@/react-query/hooks/useClick2PayIsAllowed';
import {
    addClick2PayCard,
    createClick2PayProfile,
    removeClick2PayCard,
    updateClick2PayProfileEmail,
} from '@/react-query/services/click2pay';

import { Click2PayCustomer, Click2PayProcess, Click2PayProcessStatus } from '@generated-types';

import { click2PayProfileQueryString } from '../query/use-click2pay';

interface Click2PayCreateCustomerProps {
    customer: Click2PayCustomer;
    ecid: string;
}

export type Click2PayProfileMutationResult = {
    data?: Click2PayProcess;
    variables?: Click2PayCreateCustomerProps;
    error?: RequestExceptionWithResponse;
    isPending: boolean;
    isError: boolean;
};

const c2pTimeOut = (resolve) => setTimeout(resolve, 3000);

const executeClick2PayOperation = async (
    operation: () => Promise<Click2PayProcess>,
    retryRef: React.MutableRefObject<boolean>,
): Promise<Click2PayProcess> => {
    const response = await operation();

    if (response.status === Click2PayProcessStatus.PENDING && !retryRef.current) {
        retryRef.current = true;

        await new Promise(c2pTimeOut);

        const retryResponse = await operation();
        retryRef.current = false;
        return retryResponse;
    }

    retryRef.current = false;
    return response;
};

const click2PayOperationNotAllowedError = Error('Operation not allowed!');

export const useClick2PayCreateProfileMutation = () => {
    const queryClient = useQueryClient();
    const click2PayIsAllowed = useClick2PayIsAllowed();
    const retryRef = useRef(false);

    if (!queryClient.getQueryData([click2PayProfileQueryString])) {
        queryClient.setQueryData<Click2PayProfileMutationResult>([click2PayProfileQueryString], {
            isPending: false,
            isError: false,
        });
    }

    return useMutation<Click2PayProcess, RequestExceptionWithResponse, Click2PayCreateCustomerProps>({
        mutationKey: ['click2PayProfileCreate'],
        mutationFn: async ({ customer, ecid }: Click2PayCreateCustomerProps) => {
            if (!click2PayIsAllowed) throw click2PayOperationNotAllowedError;

            return executeClick2PayOperation(() => createClick2PayProfile(customer, ecid), retryRef);
        },
        onMutate: () => {
            queryClient.setQueryData<Click2PayProfileMutationResult>([click2PayProfileQueryString], {
                isError: false,
                isPending: true,
            });
        },
        onSuccess: async (data, variables) => {
            await queryClient.invalidateQueries({ queryKey: ['click2PayProfile'] });
            queryClient.setQueryData<Click2PayProfileMutationResult>([click2PayProfileQueryString], {
                data,
                variables,
                isError: false,
                isPending: false,
            });
        },
        onError: (error, variables) => {
            retryRef.current = false;
            queryClient.setQueryData<Click2PayProfileMutationResult>([click2PayProfileQueryString], {
                error,
                variables,
                isError: true,
                isPending: false,
            });
        },
    });
};

export const useClick2PayProfileEmailUpdateMutation = () => {
    const retryRef = useRef(false);
    const queryClient = useQueryClient();
    const click2PayIsAllowed = useClick2PayIsAllowed();

    return useMutation({
        mutationKey: ['click2PayEmailUpdate'],
        mutationFn: async (email: string) => {
            if (!click2PayIsAllowed) throw click2PayOperationNotAllowedError;

            return executeClick2PayOperation(() => updateClick2PayProfileEmail(email), retryRef);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['click2PayProfile'] });
        },
        onError: () => {
            retryRef.current = false;
        },
    });
};

export const useClick2PayAddCardMutation = () => {
    const retryRef = useRef(false);
    const queryClient = useQueryClient();
    const click2PayIsAllowed = useClick2PayIsAllowed();

    return useMutation({
        mutationKey: ['click2PayCardAdd'],
        mutationFn: async (cardId: string) => {
            if (!click2PayIsAllowed) throw click2PayOperationNotAllowedError;

            return executeClick2PayOperation(() => addClick2PayCard(cardId), retryRef);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['click2PayProfile'] });
        },
        onError: () => {
            retryRef.current = false;
        },
    });
};

export const useClick2PayRemoveCardMutation = () => {
    const retryRef = useRef(false);
    const queryClient = useQueryClient();
    const click2PayIsAllowed = useClick2PayIsAllowed();

    return useMutation({
        mutationKey: ['click2PayCardRemove'],
        mutationFn: async (cardId: string) => {
            if (!click2PayIsAllowed) throw click2PayOperationNotAllowedError;

            return executeClick2PayOperation(() => removeClick2PayCard(cardId), retryRef);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['click2PayProfile'] });
        },
        onError: () => {
            retryRef.current = false;
        },
    });
};
